
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { Box, Dialog, DialogContent, IconButton, Stack, Typography } from "@mui/material";
import { useEffect, useRef, type ReactNode } from "react";
import { APP_UI } from "../ui/appUi";
import FastScrollThumb from "./FastScrollThumb";
import { useSheetDismiss } from "../hooks/useSheetDismiss";

export default function AppDialogSurface({ open, onClose, onExited, title, subtitle, children, maxWidth = "xs", zIndex = 1750, fullScreen = false }: {
  open: boolean;
  onClose: () => void;
  onExited?: () => void;
  title: string;
  subtitle?: string;
  children: ReactNode;
  maxWidth?: "xs" | "sm";
  zIndex?: number;
  fullScreen?: boolean;
}) {
  const contentRef = useRef<HTMLDivElement | null>(null);
  const { requestClose } = useSheetDismiss(open, onClose);
  useEffect(() => {
    if (!open) return;
    const frame = window.requestAnimationFrame(() => contentRef.current?.scrollTo({ top: 0, behavior: "auto" }));
    return () => window.cancelAnimationFrame(frame);
  }, [open]);
  return (
    <Dialog open={open} onClose={requestClose} disableRestoreFocus fullWidth maxWidth={maxWidth} fullScreen={fullScreen}
      slotProps={{
        transition: { onExited },
        paper: { sx: { position: "relative", borderRadius: fullScreen ? 0 : APP_UI.dialogRadius, bgcolor: APP_UI.surfaceSolid, color: APP_UI.text, backgroundImage: "none", border: fullScreen ? "none" : `1px solid ${APP_UI.border}` } },
      }}
      sx={{ zIndex, "& .MuiBackdrop-root": { bgcolor: APP_UI.backdrop } }}>
      <DialogContent ref={contentRef} sx={{ p: 2, overflowY: "auto", overscrollBehavior: "contain" }}>
        <Stack spacing={1.25}>
          <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="h6" sx={{ fontWeight: 900 }}>{title}</Typography>
              {subtitle && <Typography variant="caption" sx={{ color: APP_UI.textMuted }}>{subtitle}</Typography>}
            </Box>
            <IconButton onClick={requestClose} aria-label="Schließen" sx={{ color: "#fff", width: APP_UI.touch, height: APP_UI.touch }}><CloseRoundedIcon /></IconButton>
          </Stack>
          {children}
        </Stack>
      </DialogContent>
      <FastScrollThumb targetRef={contentRef} contained minScrollablePx={1} zIndex={zIndex + 100} />
    </Dialog>
  );
}
